import React, { forwardRef, useCallback, useRef } from 'react';
import { DraggableCore, DraggableEventHandler } from 'react-draggable';
import { twMerge } from 'tailwind-merge';

interface TrimHandleProps {
  position: 'left' | 'right';
  onDragStart: (screenX: number) => void;
  onDrag: (screenX: number) => void;
  onDragStop: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Draggable trim handle for adjusting selection start/end points.
 * Uses screenX for jitter-free dragging. Stops propagation to prevent
 * parent DraggableCore from capturing trim events.
 *
 * Accepts a forwarded ref so it can be used as a Radix Tooltip trigger (asChild).
 */
const TrimHandle = forwardRef<
  HTMLDivElement,
  TrimHandleProps &
    Omit<
      React.HTMLAttributes<HTMLDivElement>,
      'onDragStart' | 'onDrag' | 'onDragStop'
    >
>(function TrimHandle(
  {
    position,
    onDragStart,
    onDrag,
    onDragStop,
    disabled = false,
    className = '',
    ...rest
  },
  ref
) {
  const lastScreenX = useRef<number>(0);

  const getScreenX = (e: MouseEvent | TouchEvent): number => {
    if ('screenX' in e) return e.screenX;
    if ('touches' in e && e.touches.length > 0) return e.touches[0].screenX;
    return 0;
  };

  const handleStart: DraggableEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      const screenX = getScreenX(e as MouseEvent | TouchEvent);
      lastScreenX.current = screenX;
      onDragStart(screenX);
    },
    [onDragStart]
  );

  const handleDrag: DraggableEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      const screenX = getScreenX(e as MouseEvent | TouchEvent);
      onDrag(screenX);
    },
    [onDrag]
  );

  const handleStop: DraggableEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      onDragStop();
    },
    [onDragStop]
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <DraggableCore
      disabled={disabled}
      onStart={handleStart}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <div
        ref={ref}
        {...rest}
        className={twMerge(
          'absolute inset-y-0 w-2 z-20',
          'flex items-center justify-center',
          position === 'left' ? 'left-0' : 'right-0',
          disabled && 'opacity-50',
          className
        )}
        style={{ cursor: disabled ? 'not-allowed' : 'col-resize' }}
        data-testid={`trim-handle-${position}`}
        role="button"
        aria-label={`${
          position === 'left' ? 'Start' : 'End'
        } trim handle. Use arrow keys to adjust.`}
        aria-orientation="horizontal"
        tabIndex={disabled ? -1 : 0}
        onMouseDown={handleMouseDown}
        onTouchStart={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (disabled) return;
          const step = e.shiftKey ? 10 : 1;
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            onDragStart(lastScreenX.current);
            lastScreenX.current -= step;
            onDrag(lastScreenX.current);
            onDragStop();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            onDragStart(lastScreenX.current);
            lastScreenX.current += step;
            onDrag(lastScreenX.current);
            onDragStop();
          }
        }}
      />
    </DraggableCore>
  );
});

export default TrimHandle;
